// src/api/tenantPortal.js
import axiosInstance from './axiosInstance';

const API_BASE = '/api/tenant-portal';

function getToken() {
  return localStorage.getItem('accessToken') || null;
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function myRequest(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}), ...authHeaders() };
  const idempotencyKey = options.idempotencyKey || options.body?.idempotencyKey;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  try {
    const res = await axiosInstance.request({
      url: `${API_BASE}${path}`,
      method,
      headers,
      withCredentials: true,
      data: options.body ? options.body : undefined,
    });

    return res?.data ?? null;
  } catch (err) {
    const status = err?.response?.status;

    // Graceful handling for optional features (avoid crashing UI on 404s)
    if (status === 404) {
      if (path === '/tasks') return [];
      if (path === '/jobs') return [];
      if (path === '/documents') return [];
      if (path === '/rent-status') return null;
      if (path === '/manager-contact') return { notConfigured: true };
      if (path === '/property') return null;
      if (path === '/me') return null;
      return null;
    }

    const body = err?.response?.data;
    const e = new Error(`${status || ''} ${err?.response?.statusText || err?.message || 'Request failed'}`.trim());
    e.status = status;
    e.body = body;
    console.error(`[tenantPortal API] ${path} failed:`, e);
    throw e;
  }
}

// NEW: current tenant's profile (/me)
export async function fetchTenantProfile() {
  return myRequest('/me');
}

export async function fetchTenantProperty() {
  return myRequest('/property');
}

export async function fetchRentStatus() {
  return myRequest('/rent-status');
}

export async function fetchTenantTasks() {
  return myRequest('/tasks');
}

export async function fetchTenantJobs() {
  return myRequest('/jobs');
}

export async function fetchManagerContact() {
  return myRequest('/manager-contact');
}

// tenant-visible assigned documents
export async function fetchTenantDocuments() {
  return myRequest('/documents');
}

export async function submitMaintenanceRequest(data, options = {}) {
  return myRequest('/requests', {
    method: 'POST',
    body: data,
    idempotencyKey: options.idempotencyKey || data?.idempotencyKey,
  });
}

export async function respondToJobSchedule(jobId, data, options = {}) {
  return myRequest(`/jobs/${encodeURIComponent(jobId)}/schedule-response`, {
    method: 'POST',
    body: data,
    idempotencyKey: options.idempotencyKey || data?.idempotencyKey,
  });
}

export async function fetchTenantAppointment(token) {
  const res = await axiosInstance.get(`${API_BASE}/appointments/${encodeURIComponent(token)}`);
  return res?.data ?? null;
}

export async function respondToTenantAppointment(token, data, options = {}) {
  const idempotencyKey = options.idempotencyKey || data?.idempotencyKey;
  const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined;
  const res = await axiosInstance.post(
    `${API_BASE}/appointments/${encodeURIComponent(token)}/respond`,
    data,
    { headers }
  );
  return res?.data ?? null;
}

// Aggregate loader for Tenant Portal screen
export async function fetchTenantPortalData() {
  try {
    const [profile, property, rentStatus, tasks, jobs, manager, documents] = await Promise.all([
      fetchTenantProfile(),
      fetchTenantProperty(),
      fetchRentStatus(),
      fetchTenantTasks(),
      fetchTenantJobs(),
      fetchManagerContact(),
      fetchTenantDocuments(),
    ]);

    const tasksArray = tasks?.data ?? tasks;
    const jobsArray = jobs?.data ?? jobs;
    const docsArray = documents?.data ?? documents;

    return {
      profile: profile?.data ?? profile ?? null,
      property: property?.data ?? property ?? null,
      rentStatus: rentStatus?.data ?? rentStatus ?? null,
      tasks: Array.isArray(tasksArray) ? tasksArray : [],
      jobs: Array.isArray(jobsArray) ? jobsArray : [],
      manager: manager?.data ?? manager ?? null,
      documents: Array.isArray(docsArray) ? docsArray : [],
    };
  } catch (err) {
    console.error('[TenantPortal] fetch error:', err);
    throw err;
  }
}
